// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// installerInactive (#378) — the ONE shared "is this installer inactive?" test.
//
// An inactive installer is one whose Recruiter/FileMaker `Dashboard_Status_Code`
// is NOT 70 (70 = Active — the same code the candidate queries use to build the
// visible crew list). The ONLY inactive installers that ever reach the grid are
// the ones the fallback candidate query pulls in *because they still have a
// scheduled job* (see #76 / `createAdditionalCandidateQueryList`). For those we
// mirror the Compliance-Hold treatment (colorization + no-assign) so a scheduler
// can SEE the inactive installer and is stopped from assigning new work — while
// still being able to drag the existing job OFF to reassign it.
//
// WHY ITS OWN FILE (matches complianceHold.js): the test sits on both a render
// path (the grid markers) and a mutation-guard path (the drop blocks), and is
// used in several places — routing them all through one pure predicate keeps the
// visual and the block from drifting apart (the #352 lesson).
//
// Unlike Compliance Hold (date-based, on/after an effective date), inactivity is
// NOT date-scoped — an inactive installer is inactive for every day.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// FileMaker Dashboard_Status_Code for an ACTIVE installer.
export const ACTIVE_DASHBOARD_STATUS_CODE = 70;

/**
 * Is this candidate/installer inactive?
 *
 * @param {object|null|undefined} candidate a Candidates doc (needs
 *   `Dashboard_Status_Code`); tolerant of null/absent.
 * @returns {boolean} true iff the candidate exists AND its Dashboard_Status_Code
 *   is present and not 70 (Active). A missing/blank status → false (fail-open: an
 *   installer is never wrongly treated as inactive on a bad/absent value, exactly
 *   as `isComplianceHeld` fails open on a bad hold date).
 */
export function isInstallerInactive(candidate) {
  if (!candidate) return false;
  const raw = candidate.Dashboard_Status_Code;
  if (raw === undefined || raw === null || raw === "") return false;
  const code = parseFloat(raw);
  if (Number.isNaN(code)) return false;
  return code !== ACTIVE_DASHBOARD_STATUS_CODE;
}

// The alert shown when a job is dropped on an inactive installer (approved
// wording, #378). Mirrors COMPLIANCE_HOLD_ALERT's role for the inactive case.
export const INACTIVE_INSTALLER_ALERT =
  "This installer is inactive — reassign this job to an active installer.";
